import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { cleanTitle } from "@hybrid-iptv/tmdb-client";
import { api } from "../api/client.js";
import { usesTmdb } from "../discovery/rowSupplier.js";

/**
 * Artwork for Home's two HISTORY rails — Continue Watching and Recently Watched.
 *
 * Both used to show only what was saved at play time: a film's provider poster,
 * a replay's channel logo, a tuned channel's logo. Providers leave those empty or
 * dead often enough that most tiles fell back to a letter (owner, 2026-09-18:
 * "most of those items falling back to a letter have tmdb data and images"),
 * while Live Now, which asks TMDB for what is on, showed pictures. This gives the
 * history rails the same lookup: one TMDB search per title, cached for a day and
 * answered on the device in the APK like every other TMDB route.
 *
 * Under Open (Section 26 step 4) TMDB is off and TVmaze's show search takes its place for
 * the television kinds (episode, replay, channel): a show's picture, kept on the device
 * so a relaunch does not ask again. TVmaze knows no films; a film's tile keeps the
 * provider's poster saved at play time.
 */

export type HistoryKind = "vod" | "episode" | "catchup" | "channel";

export interface ArtworkRequest {
  mediaType: "movie" | "tv";
  query: string;
  year?: number;
}

/**
 * What to ask TMDB for, from a title the app saved — or undefined when the title
 * leaves nothing worth searching for.
 *
 *   vod      "The End of Oak Street - 2026"             → movie "The End of Oak Street", 2026
 *   episode  "Reacher S2E4"                              → tv "Reacher"
 *   catchup  "Question Time — UK: BBC ONE LONDON 4K ◉"   → tv "Question Time"
 *   channel  the programme on now, or a "24/7 <show>" channel's show
 */
export function artworkRequestFor(kind: HistoryKind, title: string): ArtworkRequest | undefined {
  let raw = title;
  // A replay's title is "<programme> — <channel>"; only the programme is a show.
  if (kind === "catchup") raw = raw.split(" — ")[0] ?? raw;
  // An episode's title ends in its S/E marker; the series is what TMDB knows.
  if (kind === "episode") raw = raw.replace(/\bs\d{1,3}\s*e\d{1,3}\b.*$/i, "");
  // "24/7 The Big Bang Theory" is a channel that only ever plays that show.
  if (kind === "channel") raw = raw.replace(/^\s*24\s*\/\s*7\s*/i, "");
  const { title: query, year } = cleanTitle(raw);
  if (query.length < 2) return undefined;
  // The year narrows a FILM search; a series' "year" in a provider title is
  // usually the episode's, which would exclude the show itself.
  return { mediaType: kind === "vod" ? "movie" : "tv", query, ...(kind === "vod" && year ? { year } : {}) };
}

export function artworkKey(request: ArtworkRequest): string {
  return `${request.mediaType}|${request.query.toLowerCase()}|${request.year ?? ""}`;
}

interface SearchResult {
  backdrop_path?: string | null;
  poster_path?: string | null;
}

/** The first result that has a picture, landscape first — the tiles are landscape. */
export function pickArtwork(results: readonly SearchResult[] | undefined): string | undefined {
  const hit = (results ?? []).find((result) => result.backdrop_path || result.poster_path);
  const path = hit?.backdrop_path ?? hit?.poster_path;
  return path ? `https://image.tmdb.org/t/p/w500${path}` : undefined;
}

/** One hit of TVmaze's /search/shows. */
export interface TvMazeHit {
  score?: number;
  show?: {
    id?: number;
    name?: string;
    /** First aired, YYYY-MM-DD. */
    premiered?: string | null;
    image?: { medium?: string | null; original?: string | null } | null;
  };
}

export const nameKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The words to search TVmaze for. Title cleaning turns an apostrophe into a space, and TVmaze
 * does not find "Tyler Perry s House of Payne" (seen 2026-10-12); a lone "s" is its possessive.
 */
export function tvmazeQueryFor(query: string): string {
  return query.replace(/(\w) s\b/g, "$1's");
}

/** "Doctor Who (2005)" and "Shameless (US)" are the show "Doctor Who" and "Shameless". */
export const withoutDisambiguator = (name: string) => name.replace(/\s*\((?:\d{4}|US|UK|AU|CA)\)\s*$/i, "");

/**
 * The picture of the show a TVmaze search found for a title: the first hit whose name is the
 * title (a trailing "(2005)" or "(US)" ignored) and that has a picture. A near name is not
 * taken: a wrong picture is worse than the channel's logo.
 */
export function pickTvmazeArtwork(hits: readonly TvMazeHit[] | undefined, query: string): string | undefined {
  const wanted = nameKey(query);
  if (wanted.length < 2) return undefined;
  for (const hit of hits ?? []) {
    const name = hit.show?.name;
    if (!name || nameKey(withoutDisambiguator(name)) !== wanted) continue;
    const url = hit.show?.image?.medium ?? hit.show?.image?.original;
    if (url) return url.replace(/^http:\/\//i, "https://");
  }
  return undefined;
}

/** Pictures found for television titles, kept between launches: a hit 30 days, a miss a day. */
export const TV_ART_STORE_KEY = "slq.tvArt.v1";
const TV_ART_HIT_MS = 30 * 24 * 3_600_000;
const TV_ART_MISS_MS = 24 * 3_600_000;
const TV_ART_KEEP = 250;

type TvArtStore = Record<string, [url: string, at: number]>;

function readTvArtStore(): TvArtStore {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(TV_ART_STORE_KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as TvArtStore) : {};
  } catch {
    return {};
  }
}

/** A kept answer ("" is a kept miss), or undefined when there is none or it is too old. */
export function keptTvArtwork(key: string, now = Date.now()): string | undefined {
  const entry = readTvArtStore()[key];
  if (!Array.isArray(entry) || typeof entry[0] !== "string" || typeof entry[1] !== "number") return undefined;
  const age = now - entry[1];
  return age < (entry[0] ? TV_ART_HIT_MS : TV_ART_MISS_MS) ? entry[0] : undefined;
}

export function keepTvArtwork(key: string, url: string, now = Date.now()): void {
  try {
    const store = readTvArtStore();
    store[key] = [url, now];
    const newest = Object.entries(store).sort((a, b) => b[1][1] - a[1][1]).slice(0, TV_ART_KEEP);
    localStorage.setItem(TV_ART_STORE_KEY, JSON.stringify(Object.fromEntries(newest)));
  } catch {
    // Without storage the next launch asks again.
  }
}

/** key → artwork URL, for a handful of history titles. */
export function useHistoryArtwork(requests: readonly (ArtworkRequest | undefined)[]): Map<string, string> {
  const tmdb = usesTmdb();
  const unique = useMemo(() => {
    const byKey = new Map<string, ArtworkRequest>();
    for (const request of requests) {
      if (!request) continue;
      // Open: TVmaze knows television only; a film keeps its saved poster.
      if (!tmdb && request.mediaType !== "tv") continue;
      byKey.set(artworkKey(request), request);
    }
    return [...byKey.entries()];
  }, [requests, tmdb]);

  const results = useQueries({
    queries: unique.map(([key, request]) => ({
      queryKey: [tmdb ? "tmdb" : "tvmaze", "history-artwork", key],
      queryFn: async (): Promise<string> => {
        if (tmdb) {
          const answer = await api.post<{ results: SearchResult[] }>("/api/tmdb/search", {
            mediaType: request.mediaType,
            query: request.query,
            ...(request.year ? { year: request.year } : {}),
          });
          return pickArtwork(answer.results) ?? "";
        }
        const kept = keptTvArtwork(key);
        if (kept !== undefined) return kept;
        const hits = await api.get<TvMazeHit[]>(`/api/tv/search?q=${encodeURIComponent(tvmazeQueryFor(request.query))}`);
        const url = pickTvmazeArtwork(hits, request.query) ?? "";
        keepTvArtwork(key, url);
        return url;
      },
      staleTime: 1000 * 60 * 60 * 24, // a title's picture rarely changes
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });

  // Built each render rather than memoised: it is a handful of entries, and
  // useQueries hands back a new array every render anyway.
  const map = new Map<string, string>();
  unique.forEach(([key], i) => {
    const url = results[i]?.data;
    if (url) map.set(key, url);
  });
  return map;
}
